import { LitElement, html, nothing } from 'lit';
import { styleMap } from 'lit/directives/style-map.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { uid } from 'uid';
import {
  calculatePosition,
  selectorTabbable,
  getDeviceType,
  MEDIUM,
  SMALL,
  debounce,
} from '@fil-global/shared';
import { dispatchEvent } from '@fil-global/shared/src/util';
import '@fil-global/gds-streamline-icons/webcomponent/fil-icon-close';
import '@fil-global/gds-streamline-icons/webcomponent/fil-icon-custom-info-circle';

import componentStyles from './fil-tooltip.styles.js';

/**
 * @tag fil-tooltip
 * @property {boolean} [advanced] Whether to use advanced variation of the tooltip
 * @property {object} [advancedLabel] Details of the advancedLabel
 * @property {'left'|'right'|'center'} [alignment] Alignment of the tooltip
 * @property {string} [label] Label of the component
 * @property {'top'|'bottom'} [position] Position of the tooltip
 * @property {number} [size] Size of the tooltip
 * @property {boolean} [clickOpen] Allows the tooltip to remain open if triggered with a click.
 */
export class FilTooltip extends LitElement {
  static properties = {
    advanced: { type: Boolean },
    advancedLabel: { type: Object },
    alignment: { type: String }, // "center" | "left" | "right"
    label: { type: String },
    // Deprecated: Use slot instead, will be removed in the future, it can not support html content.
    message: { type: String },
    position: { type: String },
    size: { type: Number },
    debounce: { type: Number },
    clickOpen: { type: Boolean },
    _filled: { type: Boolean, attribute: false },
    _show: { type: Boolean, attribute: false },
    _style: { type: String, attribute: false },
  };

  static styles = [componentStyles];

  constructor() {
    super();
    this.alignment = 'center';
    this.advanced = false;
    this.advancedLabel = {
      title: '',
      clickable: false,
    };
    this.debounce = 200;
    this.position = 'bottom';
    this.size = 20;

    this._filled = false;
    this._show = false;
    this._eventHandled = false;
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleCloseOthers = this.handleCloseOthers.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.isClick = false;
    this.isFocus = false;
    this.uid = uid(6);
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('click', this.handleClickOutside);
    window.addEventListener('keydown', this.handleKeydown);
    window.addEventListener('close.gds.trigger', this.handleCloseOthers);
    window.addEventListener('resize', debounce(this.handleResize, 300));
  }

  disconnectedCallback() {
    window.removeEventListener('click', this.handleClickOutside);
    window.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener('close.gds.trigger', this.handleCloseOthers);
    window.removeEventListener('resize', this.handleResize);
    super.disconnectedCallback();
  }

  handleResize() {
    const isMobile = getDeviceType() === SMALL || getDeviceType() === MEDIUM;
    if (isMobile) {
      this.size = 16;
    } else {
      this.size = 20;
    }
  }

  handleCloseOthers(event) {
    if (this.uid === event.detail.id) {
      return;
    }
    this.hide();
  }

  handleClickOutside(e) {
    const tooltipIcon = this.shadowRoot.querySelector('fil-icon-custom-info-circle');
    const path = e.path || (e.composedPath && e.composedPath());
    if (this._show && !path.includes(tooltipIcon)) {
      this.hide();
    }
  }

  handleKeydown(e) {
    if (e.key === 'Escape') {
      this.hide();
    }
  }

  handleKeyEvents(e) {
    if (e.key === 'Enter' || e.key === ' ' || e.code === 'Space') {
      e.preventDefault();
      this.show();
    } else if (e.key === 'Tab') {
      const messageContainer = this.shadowRoot.querySelector('.fil-tooltip__msg');
      let focusableElement = messageContainer.querySelector(selectorTabbable);
      if (focusableElement && !e.shiftKey) {
        e.preventDefault();
        focusableElement.focus();
      } else this.hide();
    }
  }

  updated(changedProperties) {
    if (changedProperties.has('advanced')) {
      this.clickOpen = this.advanced || (!this.advanced && this.clickOpen) ? true : false;
    }
  }

  handleMouseover(e) {
    e.stopPropagation();
    this._filled = true;
  }

  handleMouseout(e) {
    e.stopPropagation();
    if (!this._show) {
      this._filled = false;
    }
  }

  handleMouseenter() {
    const { clickOpen, advanced, _show } = this;
    let disableHover = advanced || clickOpen ? true : false;
    this._filled = true;

    if (!disableHover) {
      if (!_show) {
        this.timeout = setTimeout(() => {
          this.show();
        }, 200);
      }
    }
  }

  handleMouseleave() {
    const { clickOpen, advanced, isFocus, isClick } = this;
    let disableHover = advanced || clickOpen ? true : false;

    // hover trigger
    if (!disableHover) {
      clearTimeout(this.timeout);
      if (!isFocus || (isClick && !clickOpen)) {
        this.hide();
        this._filled = false;
      }
    } else {
      // click
      if (!isClick) {
        this._filled = false;
      }
    }
  }

  handleMousedown() {
    const { clickOpen, _show, advanced, isClick } = this;
    let disableHover = advanced || clickOpen ? true : false;
    if (clickOpen) {
      if (isClick) {
        //this.hide();
        // this.isClick = false;
      } else {
        this.isClick = true;
        if (disableHover && !_show) {
          this.show();
        }
      }
    } else {
      this.isClick = true;
    }
  }

  handleFocus() {
    this.isFocus = true;
    if (this.isClick) {
      // If we're not showing open on clicks, we need to pretend a click-launched focus isn't
      // a real focus, otherwise on hover and come back we get bad behavior
      if (!this.clickOpen) {
        this.isFocus = false;
      }
      return false;
    } else {
      // advanced focus fires close icon color change.
      if (this.advanced) {
        this.isClick = true;
      }
      this.show();
    }
  }

  handleTouch() {
    const hasTouch = 'ontouchstart' in window || typeof window.ontouchstart !== 'undefined';

    if (hasTouch) {
      this.toggle();
    }
  }

  show() {
    // Handle focus and click events are triggered together, that cases the tooltip closes immediately.
    if (this._eventHandled) return;
    this._eventHandled = true;

    // close other tooltip if there is any
    dispatchEvent(this, 'close.gds.trigger', { id: this.uid });

    // calculate tooltip position
    const messageContainer = this.shadowRoot.querySelector('.fil-tooltip__msg');
    messageContainer.style.display = 'block';
    this._filled = true;
    this._show = true;

    const dimension = messageContainer.getBoundingClientRect();

    let element = this.shadowRoot.querySelector('fil-icon-custom-info-circle');
    const calculateStyle = calculatePosition(element, messageContainer, {
      position: this.position,
      alignment: this.alignment,
      messageHeight: dimension.height,
      messageWidth: dimension.width,
    });
    this._style = `${calculateStyle.style} visibility:${this._show ? 'visible' : 'hidden'};`;
    this.position = calculateStyle.data.position || this.position;
    this._flipAlignment = calculateStyle.data.alignment || this.alignment;
    this._windowCenter = calculateStyle.data.windowCenter;

    if (calculateStyle.data.windowCenter) {
      const r = this.shadowRoot.host;
      r.style.setProperty('--tooltip-body-center', `${-calculateStyle.data.left + 10}px`);
    }
    let focusableElement = messageContainer.querySelector(selectorTabbable);
    if (focusableElement) focusableElement.focus();

    dispatchEvent(this, 'show.gds.tooltip');

    setTimeout(() => {
      this._eventHandled = false;
    }, this.debounce);
  }

  /**
   * Hides the current tooltip, and resets the positioning class if it was changed due to collision
   * @fires Tooltip#hide
   * @function
   */
  hide() {
    this.isFocus = false;
    this.isClick = false;
    this._filled = false;
    this._flipAlignment = null;
    this._windowCenter = null;
    this._style = 'display: none;';
    this._show = false;
    dispatchEvent(this, 'close.gds.tooltip');
  }

  toggle() {
    if (this._show) {
      this.hide();
    } else {
      this.show();
    }
  }

  firstUpdated() {
    this._style = 'display: none';
  }

  getTooltipLabel() {
    let label = '';
    switch (this.advancedLabel.kind) {
      case 'link':
        label = html`<fil-link href="javascript:void(0)">${this.advancedLabel.title}</fil-link>`;
        break;
      case 'text':
        label = html`<span>${this.advancedLabel.title}</span>`;
        break;
      default:
        label = html`<b>${this.advancedLabel.title}</b>`;
    }
    return label;
  }

  render() {
    if (this.label && !this.advancedLabel.title) {
      this.advancedLabel.title = this.label;
    }
    const classes = {
      'fil-tooltip': true,
      'fil-tooltip--margin-left': !!(this.label || this.advancedLabel.title),
      [`fil-tooltip--advanced`]: !!this.advanced,
    };

    const tip = {
      [`fil-tooltip__icon`]: true,
      [`no-margin`]: !(this.label || this.advancedLabel.title),
      [`fil-tooltip__icon--hover`]: this._filled && !this.isClick,
      [`fil-tooltip__icon--focus`]: this._filled && this.isClick,
    };
    const style = `${this._style};`;

    return html`
      <div class="fil-tooltip-wrapper">
        ${this.advancedLabel.title
          ? html`<span
              class="fil-tooltip__label"
              tabindex="0"
              role="link"
              @click="${e => {
                if (this.advancedLabel.clickable) {
                  this.toggle();
                  e.stopPropagation();
                }
              }}"
              @keypress="${e => {
                if (this.advancedLabel.clickable) {
                  this.toggle();
                  e.stopPropagation();
                }
              }}"
              @mouseover="${this.handleMouseover}"
              @mouseout="${this.handleMouseout}"
              >${this.getTooltipLabel()}</span
            >`
          : nothing}
        <div class=${classMap(classes)}>
          ${this.advancedLabel.kind || this.advancedLabel.kind !== 'link'
            ? html`<fil-icon-custom-info-circle
                class=${classMap(tip)}
                style=${styleMap({ lineHeight: `${this.size}px` })}
                ?filled="${this._filled}"
                color=${ifDefined(
                  this._filled ? (this.isClick ? 'blue-700' : 'blue-500') : undefined,
                )}
                size="${this.size}"
                tabindex="0"
                role="button"
                title="Tooltip Icon"
                @mouseenter=${this.handleMouseenter}
                @mouseleave=${this.handleMouseleave}
                @mousedown=${this.handleMousedown}
                @focus=${this.handleFocus}
                @keydown="${this.handleKeyEvents}"
                @touchend="${this.handleTouch}"
              >
              </fil-icon-custom-info-circle>`
            : nothing}
          <div
            class="fil-tooltip__msg fil-tooltip__msg--${this.position} align-${this._flipAlignment
              ? this._flipAlignment
              : this.alignment} ${this._windowCenter ? 'viewpoint-middle' : ''}"
            role="tooltip"
            aria-hidden="${!!this._show}"
            style=${style}
            @click=${e => {
              e.stopPropagation();
            }}
          >
            ${this.advanced
              ? html`<div class="fil-tooltip__msg--body">
                  <button class="fil-tooltip--close" @click=${this.hide}>
                    <fil-icon-close
                      size="16"
                      aria-label="Close"
                      title="Close"
                      aria-hidden="true"
                    ></fil-icon-close></button
                  ><slot></slot>
                </div>`
              : html`<slot>${this.message}</slot>`}
          </div>
        </div>
      </div>
    `;
  }
}

export default FilTooltip;
